import { ref, nextTick } from 'vue';

export function useSVGInteraction(getCategorySymbols = null) {
  const showModal = ref(false);
  const selectedIsotope = ref(null);
  const BASE_TILE_SELECTOR = ':scope > path:not([data-animated-clone]), :scope > rect:not([data-animated-clone])';

  const normalizeSymbol = (value) => {
    if (typeof value !== 'string') return null;
    const normalized = value.trim().toLowerCase();
    return normalized || null;
  };

  const toSymbolSet = (values) => {
    if (!Array.isArray(values)) return new Set();

    return new Set(
      values
        .map((value) => {
          if (typeof value === 'string') return normalizeSymbol(value);
          if (value && typeof value === 'object') return normalizeSymbol(value.symbol);
          return null;
        })
        .filter(Boolean)
    );
  };

  const getCategorySymbolSets = () => {
    if (typeof getCategorySymbols !== 'function') return {};

    const categorySymbols = getCategorySymbols() || {};
    return Object.fromEntries(
      Object.entries(categorySymbols).map(([categoryId, values]) => [
        categoryId,
        toSymbolSet(values)
      ])
    );
  };

  const getSymbolCategoryId = (symbol, categorySymbolSets) => {
    const normalizedSymbol = normalizeSymbol(symbol);
    if (!normalizedSymbol) return null;

    if (categorySymbolSets.current_portfolio?.has(normalizedSymbol)) return 'current_portfolio';
    if (categorySymbolSets.rd_precursor?.has(normalizedSymbol)) return 'rd_precursor';
    if (categorySymbolSets.other_tech?.has(normalizedSymbol)) return 'other_tech';
    return null;
  };

  const getCategoryStyle = (categoryId) => {
    if (categoryId === 'current_portfolio') {
      return { fill: '#96FDEB', text: '#000000' };
    }
    if (categoryId === 'rd_precursor') {
      return { fill: '#F5CEFF', text: '#000000' };
    }
    if (categoryId === 'other_tech') {
      return { fill: '#2E4AA4', text: '#FFFFFF' };
    }
    if (categoryId === 'uncategorized') {
      return { fill: '#CBD1FF', text: '#1E2442' };
    }
    return null;
  };

  const applyGroupStyle = (group, categoryId) => {
    const style = getCategoryStyle(categoryId);
    if (!style) return;

    const firstShape = group.querySelector(BASE_TILE_SELECTOR);
    if (!firstShape) return;

    firstShape.setAttribute('fill', style.fill);

    const otherPaths = group.querySelectorAll('path');
    const otherRects = group.querySelectorAll('rect');
    [...otherPaths, ...otherRects].forEach((element) => {
      if (element === firstShape || element.getAttribute('data-animated-clone') === 'true') return;
      element.setAttribute('fill', style.text);
    });

    const textElements = group.querySelectorAll('text, tspan');
    textElements.forEach((element) => {
      element.setAttribute('fill', style.text);
    });
  };

  const setupSVGInteractions = (isotopes, hasActiveFilters = null) => {
    nextTick(() => {
      // Get the SVG element
      const svg = document.getElementById('periodic_table');
      if (!svg) {
        console.warn('SVG with id "periodic_table" not found');
        return;
      }
      
      // Update all SVG tile colors first
      updateSVGTileColors(svg, isotopes);
      
      // Delay animation setup to ensure SVG is fully rendered
      setTimeout(() => {
        // Then setup interactions for active isotopes with staggered animations
        if (isotopes && isotopes.length) {
          isotopes.forEach((isotope, index) => {
            const group = svg.querySelector(`g#${isotope.symbol}`);
            if (group) {
              // Stagger each animation by 100ms
              wrapGroupWithAnchor(group, isotope, index * 0.1, hasActiveFilters);
            }
          });
        }
      }, 300); // Wait 300ms for SVG to fully render
    });
  };

  const wrapGroupWithAnchor = (group, isotope, delaySeconds = 0, hasActiveFilters = null) => {
    // Check if already wrapped
    if (group.parentElement.tagName.toLowerCase() === 'a') return;
    
    // Create anchor element
    const anchor = document.createElementNS('http://www.w3.org/2000/svg', 'a');
    anchor.setAttribute('href', '#');
    anchor.setAttribute('data-isotope-id', isotope.id);
    anchor.classList.add('isotope_svg', 'clickable');

    // Add animation to the first tile shape element (background tile)
    const firstShape = group.querySelector(BASE_TILE_SELECTOR);
    if (firstShape) {
      // Clone the first tile for the growing/fading effect
      const clonedShape = firstShape.cloneNode(true);
      clonedShape.setAttribute('opacity', '0.7');
      
      // Simply clone the shape and add animations directly to it
      // This keeps it in the same coordinate space as the original
      const animatedShape = clonedShape.cloneNode(true);
      animatedShape.setAttribute('data-animated-clone', 'true');
      
      // Get the transform attribute from the original shape to maintain positioning
      const originalTransform = firstShape.getAttribute('transform') || '';
      animatedShape.setAttribute('transform', originalTransform);
      
      // Get the center point of the shape for scaling
      const bbox = firstShape.getBBox();
      const centerX = bbox.x + bbox.width / 2;
      const centerY = bbox.y + bbox.height / 2;
      
      // Add scale animation from center point with staggered delay
      const scaleAnimation = document.createElementNS('http://www.w3.org/2000/svg', 'animateTransform');
      scaleAnimation.setAttribute('attributeName', 'transform');
      scaleAnimation.setAttribute('begin', `${delaySeconds}s`);
      scaleAnimation.setAttribute('dur', '2s');
      scaleAnimation.setAttribute('type', 'scale');
      scaleAnimation.setAttribute('values', `1; 1.2; 1`);
      scaleAnimation.setAttribute('repeatCount', 'indefinite');
      scaleAnimation.setAttribute('additive', 'sum');
      
      // Set transform origin to center
      animatedShape.style.transformOrigin = `${centerX}px ${centerY}px`;
      
      // Add fade out animation to the animated shape with same delay
      const fadeAnimation = document.createElementNS('http://www.w3.org/2000/svg', 'animate');
      fadeAnimation.setAttribute('attributeName', 'opacity');
      fadeAnimation.setAttribute('begin', `${delaySeconds}s`);
      fadeAnimation.setAttribute('dur', '2s');
      fadeAnimation.setAttribute('from', '0.7');
      fadeAnimation.setAttribute('to', '0');
      fadeAnimation.setAttribute('repeatCount', 'indefinite');
      
      animatedShape.appendChild(scaleAnimation);
      animatedShape.appendChild(fadeAnimation);
      
      // Insert animated shape before the original shape (so it appears behind)
      group.insertBefore(animatedShape, firstShape);
    }
    
    const shouldApplyHoverEffect = () => {
      if (!hasActiveFilters || typeof hasActiveFilters !== 'object') {
        return true;
      }
      return !hasActiveFilters.value;
    };

    // Add hover effects
    anchor.addEventListener('mouseenter', () => {
      if (!shouldApplyHoverEffect()) return;
      group.style.opacity = '0.8';
      group.style.transition = 'all 0.2s ease';
    });
    
    anchor.addEventListener('mouseleave', () => {
      if (!shouldApplyHoverEffect()) return;
      group.style.opacity = '1';
    });
    
    // Add click handler
    anchor.addEventListener('click', (e) => {
      e.preventDefault();
      openModal(isotope);
    });
    
    // Wrap the group
    group.parentNode.insertBefore(anchor, group);
    anchor.appendChild(group);
  };

  const updateSVGTileColors = (svg, isotopes) => {
    // Get all groups in the SVG
    const allGroups = Array.from(svg.querySelectorAll('g[id]')).filter(
      (group) => Boolean(group.querySelector(BASE_TILE_SELECTOR))
    );

    const categorySymbolSets = getCategorySymbolSets();
    const hasCategorySymbols = Object.keys(categorySymbolSets).length > 0;
    
    // Create a set of active isotope symbols for quick lookup
    const activeSymbols = new Set(
      isotopes ? isotopes.map((isotope) => normalizeSymbol(isotope.symbol)).filter(Boolean) : []
    );
    
    allGroups.forEach(group => {
      const groupId = group.getAttribute('id');
      if (!groupId) return;

      if (hasCategorySymbols) {
        const categoryId = getSymbolCategoryId(groupId, categorySymbolSets) || 'uncategorized';
        applyGroupStyle(group, categoryId);
        return;
      }
      
      // Find the first tile shape in this group (the background tile)
      const firstShape = group.querySelector(BASE_TILE_SELECTOR);
      if (!firstShape) return;
      
      // Update fill color based on whether isotope is active
      if (activeSymbols.has(normalizeSymbol(groupId))) {
        // Isotope is active - use light blue for background
        firstShape.setAttribute('fill', '#96FDEB');
        // Get all other paths and rects (not the first tile shape which is background)
        const otherPaths = group.querySelectorAll('path');
        const otherRects = group.querySelectorAll('rect');
        [...otherPaths, ...otherRects].forEach(element => {
          if (element === firstShape || element.getAttribute('data-animated-clone') === 'true') return;
          element.setAttribute('fill', '#000000');
        });
        const textElements = group.querySelectorAll('text, tspan');
        textElements.forEach(element => {
          element.setAttribute('fill', '#000000');
        });
      } else {
        const currentFill = firstShape.getAttribute('fill');
        if (currentFill != '#f5ceff' && currentFill != '#F5CEFF' 
          && currentFill != '#2E4AA4' && currentFill != '#2E4AA4' 
          && currentFill != '#7f73e6' && currentFill != '#7F73E6') {
          firstShape.setAttribute('fill', '#7F73E6');
          const otherPaths = group.querySelectorAll('path');
          const otherRects = group.querySelectorAll('rect');
          [...otherPaths, ...otherRects].forEach(element => {
            if (element === firstShape || element.getAttribute('data-animated-clone') === 'true') return;
            element.setAttribute('fill', '#ffffff');
          });
          const textElements = group.querySelectorAll('text, tspan');
          textElements.forEach(element => {
            element.setAttribute('fill', '#ffffff');
          });
        }
      }
    });
  };

  const openModal = (isotope) => {
    selectedIsotope.value = isotope;
    showModal.value = true;
  };

  const closeModal = () => {
    showModal.value = false;
    selectedIsotope.value = null;
  };

  return {
    showModal,
    selectedIsotope,
    setupSVGInteractions,
    openModal,
    closeModal
  };
}
